import React, { lazy, Suspense, useEffect, useState } from "react";
import Loader from "../components/Loader";
import { apiFetch } from "../services/api/Common";

// ======================
// Types
// ======================

interface SliderItem {
  id: number;
  title: string;
  highlight: string;
  tagline: string;
  description: string;
  background_image_url: string;
  button_name: string;
  target_url: string | null;
}

interface Testimonial {
  id: number;
  name: string;
  title: string;
  position: string;
  association_name: string;
  tagline: string;
  description: string;
  long_description: string;
  image_url: string;
}

interface Settings {
  our_vision: string;
  [key: string]: string;
}

interface HomeResponse {
  success: boolean;
  top_slider: {
    data: SliderItem[];
  };
  bottom_slider: {
    data: SliderItem[];
  };
  top_testimonial: {
    data: Testimonial[];
  };
  bottom_testimonial: {
    data: Testimonial[];
  };
  settings: Settings;
}

// ======================
// Lazy Imports
// ======================

const PersonalityCard = lazy(() =>
  import("../components/PersonalityCard").then((module) => ({
    default: module.PersonalityCard,
  })),
);

const AthleteResources = lazy(() =>
  import("../components/venus&facilities/AthleteResources").then((module) => ({
    default: module.AthleteResources,
  })),
);

const HaryanaVenuesGallery = lazy(() =>
  import("../components/venus&facilities/SportsGallery").then((module) => ({
    default: module.HaryanaVenuesGallery,
  })),
);

const EventsSection = lazy(() =>
  import("../components/hoaUpComingEvents/EventsSection").then((module) => ({
    default: module.EventsSection,
  })),
);

const DignitariesSection = lazy(() =>
  import("../components/DignitariesSection").then((module) => ({
    default: module.DignitariesSection,
  })),
);

const ImageCarousel = lazy(() =>
  import("../components/slider/ImageCarousel").then((module) => ({
    default: module.ImageCarousel,
  })),
);

const MainImage = lazy(() =>
  import("../components/slider/MainImage").then((module) => ({
    default: module.MainImage,
  })),
);

const HeroSection = lazy(() =>
  import("../components/HeroSection").then((module) => ({
    default: module.HeroSection,
  })),
);

const NewsSection = lazy(() =>
  import("../components/combinedSectionhome/NewsSection").then((module) => ({
    default: module.CombinedHOASection,
  })),
);

const AboutUs = lazy(() => import("../components/combinedSectionhome/AboutUs"));

const WhoWeAre = lazy(
  () => import("../components/combinedSectionhome/WhoWeAre"),
);

const MediaHighlights = lazy(
  () => import("../components/combinedSectionhome/MediaHighlights"),
);

const SocialMediaPage = lazy(
  () => import("../components/combinedSectionhome/SocialMedia"),
);

const Vision = lazy(() => import("../components/combinedSectionhome/Vision"));
const NotificationMarquee = lazy(
  () => import("../components/combinedSectionhome/NotificationMarquee"),
);

// ======================
// Component
// ======================

const HomePage: React.FC = () => {
  const [homeData, setHomeData] = useState<any>(null);
  const [bannerData, setBannerData] = useState<any>(null);
  const [athleteResourcesData, setAthleteResourcesData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(true);

        const [homeResponse, bannerResponse] = await Promise.all([
          apiFetch("home"),
          apiFetch("site-banner"),
        ]);
        const athleteResourcesResponse = await apiFetch(
          "pages/athlete-resources",
        );

        setHomeData(homeResponse.data);
        setBannerData(bannerResponse.data);
        setAthleteResourcesData(athleteResourcesResponse.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  const banners: string[] = Object.entries(homeData?.settings || {})
    .filter(
      ([key, value]) =>
        key.startsWith("home_page_banner") && typeof value === "string",
    )
    .map(([, value]) => value as string);

  const newsItems: string[] =
    bannerData?.social_galleries?.data[0]?.children?.map(
      (item: any) => item.title,
    ) ||
    [] ||
    [];

  if (loading) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader />}>
      {/* Hero Banner */}
      <MainImage topSlides={homeData?.top_slider?.data || []} />
      {/* About HOA */}
      <WhoWeAre
        title="Haryana Olympic Association"
        description="The Haryana Olympic Association (HOA), established in 1966 and affiliated with the Indian Olympic Association (IOA), is the apex autonomous sports body of the State of Haryana. Guided by the ideals of the Olympic Movement and the Olympic Charter, HOA is dedicated to the promotion and development of sports across the state."
        buttonText="Learn More About HOA"
      />

      {/* Slider Data */}
      <ImageCarousel bottomSlides={homeData?.bottom_slider?.data || []} />
      <AboutUs />

      {/* Testimonials */}
      <PersonalityCard
        topTestimonials={homeData?.top_testimonial?.data || []}
      />
      {newsItems.length > 0 && <NotificationMarquee messages={newsItems} />}

      <MediaHighlights topBanner={bannerData?.top_banner} />

      <EventsSection news={bannerData?.news?.images || []} />
      {/* Vedios */}
      <DignitariesSection />

      <SocialMediaPage banners={banners} />
      {/* Vision Data */}
      <Vision content={homeData?.settings?.our_vision || ""} />

      {/* Optional */}
      {/* <HaryanaVenuesGallery /> */}

      <AthleteResources data={athleteResourcesData?.data || []} />
    </Suspense>
  );
};

export default HomePage;
